/**
 * Which footnotes a document has, and what number each one carries.
 *
 * COMPUTED, NEVER STORED — the same rule as heading numbers, for the same reason. "3" is not a fact
 * about a note, it is a fact about how many notes precede it. Store it and it is wrong the moment
 * somebody adds a note earlier in the document, which is exactly when nobody re-reads the ones
 * after it.
 *
 * DOCUMENT ORDER, WHEREVER THEY SIT. A footnote inside a table cell or a list item is still a
 * footnote — unlike a heading, which is a section of the procedure and is only counted at the top
 * level. A note is about the sentence it is attached to, and sentences live inside cells.
 *
 * MIRRORED IN document_rendition.py. A note numbered 3 on screen and 2 in the PDF is worse than no
 * numbering at all.
 */

export type Note = {
    /** What the reader sees: 1, 2, 3 … and back to 1 at each section when the document asks. */
    number: number;
    /** What the note says. */
    text: string;
    /**
     * Where it falls among ALL the notes, from 0. Counted even when the numbers restart.
     *
     * TWO NOTES CAN BOTH BE "1" once numbering restarts per section, so the number stops being an
     * identity. Anything that has to keep notes in the order a reader meets them -- the band at the
     * foot of a sheet, most obviously -- sorts on this and displays the other.
     */
    order: number;
};

/** How a document numbers its footnotes. */
export type FootnoteNumbering = "continuous" | "section" | "page";

/** What a document is asking for, from whatever its attribute holds. */
export function footnoteNumbering(attrs: any): FootnoteNumbering {
    const held = attrs?.footnoteNumbering;
    return held === "section" || held === "page" ? held : "continuous";
}

/**
 * Renumber from 1 on each page.
 *
 * SEPARATE FROM notesOf, AND IT HAS TO BE. Which section a note is in is a property of the
 * DOCUMENT and can be read off the body; which page it is on is a property of the LAYOUT and
 * cannot. So this takes the answer from whatever has just measured -- the pagination pass on
 * screen, the previous render pass in the PDF -- and applies it to notes that have already been
 * found and ordered.
 *
 * A NOTE WHOSE PAGE IS NOT KNOWN YET KEEPS THE PAGE BEFORE IT. On the first frame, before anything
 * has been measured, nothing is known and the whole document numbers as one page -- which is
 * continuous numbering, and is the right thing to show for the fraction of a second before the
 * measurement arrives. Numbering it 1, 1, 1, 1 instead would flicker.
 */
export function numberedByPage(notes: Note[], pageOf: Map<number, number>): Note[] {
    const out: Note[] = [];
    let page: number | null = null;
    let within = 0;
    notes.slice().sort((a, b) => a.order - b.order).forEach((note) => {
        const sheet = pageOf.has(note.order) ? (pageOf.get(note.order) as number) : page;
        if (page !== null && sheet !== page) within = 0;
        if (sheet !== null) page = sheet;
        within += 1;
        out.push({ ...note, number: within });
    });
    return out;
}

/**
 * Every footnote in the document, in the order a reader meets them.
 *
 * `restart` NUMBERS FROM 1 AGAIN AT EACH SECTION. A section is a TOP-LEVEL Heading 1 and nothing
 * else -- the same rule the outline uses, and for the same reason: a heading inside a table cell or
 * a list item is not a section of the procedure, and restarting the notes there would renumber a
 * document because somebody put a title in a box.
 *
 * THE WALK IS STILL ONE WALK. The restart is a counter reset, not a second pass, so a note's
 * `order` is unaffected by it and the two numbers cannot drift apart.
 */
export function notesOf(body: any, restart = false): Note[] {
    const notes: Note[] = [];
    let within = 0;

    const walk = (node: any, top: boolean) => {
        if (!node || typeof node !== "object") return;
        if (restart && top && node.type === "heading" && Number(node.attrs?.level) === 1) {
            within = 0;
        }
        if (node.type === "footnote") {
            within += 1;
            notes.push({ number: restart ? within : notes.length + 1,
                         text: (node.attrs?.text ?? "").trim(), order: notes.length });
            return;
        }
        (node.content ?? []).forEach((child: any) => walk(child, top && node.type === "doc"));
    };

    walk(body, true);
    return notes;
}

/** A marker the browser has already laid out: which note it is, and how far down the paper it sits. */
export type Marker = { number: number; text: string; top: number; order?: number };

/** The notes that belong at the foot of one sheet. */
export type Band = { sheet: number; notes: Note[] };

/**
 * Group the markers by the sheet they landed on.
 *
 * THE SHEET IS THE MARKER'S, NEVER THE NOTE'S. That is the whole difficulty of a footnote and the
 * reason this one waited for pagination: a note is placed by where its reference ended up, which is
 * a fact about the layout and changes every time somebody types a paragraph above it.
 *
 * ORDERED BY THE READER'S ORDER WITHIN A SHEET, not by measured position and not by the number.
 * Two markers on one line come back from the DOM at the same `top`, so a sort on that is a sort on
 * nothing. And once numbering restarts per section the NUMBER is not an order either: two notes on
 * one page can both be "1", and sorting on that would put them in whichever order the map happened
 * to hold. `order` is the count across the whole document and is the only thing that is always
 * monotonic.
 */
export function bandsFor(markers: Marker[], pitch: number): Band[] {
    const bySheet = new Map<number, Note[]>();
    markers.forEach((marker) => {
        const sheet = Math.max(0, Math.floor(marker.top / pitch));
        if (!bySheet.has(sheet)) bySheet.set(sheet, []);
        bySheet.get(sheet)!.push({ number: marker.number, text: marker.text,
                                   order: marker.order ?? marker.number });
    });
    return Array.from(bySheet.entries())
        .map(([sheet, notes]) => ({ sheet, notes: notes.slice().sort((a, b) => a.order - b.order) }))
        .sort((a, b) => a.sheet - b.sheet);
}

/**
 * Merge a freshly measured set of reserves into the ones already in the layout, keeping the larger.
 *
 * MONOTONE WITHIN A SETTLING RUN, AND THIS IS NOT TIDINESS -- IT IS WHAT TERMINATES. Reserving room
 * at the foot of a sheet can push the very marker that asked for it onto the next sheet; the reserve
 * then has nothing to hold, drops to zero, and the marker comes straight back. That is a loop with
 * no fixed point, and in a browser it is a page that stops responding to clicks.
 *
 * Letting a reserve only GROW until the document itself changes bounds the run: reserves rise, text
 * moves later, and nothing moves back. The cost is a band of blank paper at the foot of a sheet
 * whose note has moved on, until the next keystroke -- which is invisible, and is the right thing to
 * trade for a loop.
 */
export function grown(current: Map<number, number>, measured: Map<number, number>): Map<number, number> {
    const next = new Map(current);
    measured.forEach((height, sheet) => {
        next.set(sheet, Math.max(next.get(sheet) ?? 0, height));
    });
    return next;
}
